import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import '../components/aiinterview/aiinterview.css';
import '../components/videoroom/videoroom.css';
import { errMsg } from '../lib/aiInterviewApi';
import { roomAPI } from '../lib/roomApi';
import { browserTz } from '../lib/schedulingApi';
import { Pill } from '../components/aiinterview/recruiter/common';
import { useDailyCall } from '../components/videoroom/useDailyCall';
import { useCopilotCapture } from '../components/videoroom/useCopilotCapture';
import StaffStage from '../components/videoroom/StaffStage';
import { useMediaStream } from '../components/aiinterview/media';
import CopilotPanel from '../components/videoroom/CopilotPanel';
import ScorecardPanel from '../components/videoroom/ScorecardPanel';
import BriefPanel from '../components/videoroom/BriefPanel';
import { fmtClockTime } from '../components/videoroom/time';

const TABS = [['copilot', 'Copilot'], ['scorecard', 'Scorecard'], ['brief', 'Brief']];
const STATE_PILL = { early: ['Opens soon', 'sky'], open: ['Room open', 'green'], closed: ['Closed', 'slate'], cancelled: ['Cancelled', 'red'], not_booked: ['Not booked', 'slate'] };

const Side = ({ ctx, tab, setTab, capture, live, onConsent }) => (
  <aside className="vr-side" data-testid="room-side-panel">
    <div className="vr-tabs" role="tablist">
      {TABS.map(([k, l]) => <button type="button" key={k} role="tab" className={tab === k ? 'is-on' : ''} onClick={() => setTab(k)} data-testid={`room-tab-${k}`}>{l}</button>)}
    </div>
    <div className="vr-side-scroll">
      <div hidden={tab !== 'copilot'}><CopilotPanel bookingId={ctx.booking.id} capture={capture} live={live} onConsent={onConsent} /></div>
      <div hidden={tab !== 'scorecard'}><ScorecardPanel bookingId={ctx.booking.id} /></div>
      <div hidden={tab !== 'brief'}><BriefPanel ai={ctx.ai} competencies={ctx.competencies} /></div>
    </div>
  </aside>
);

export default function HumanInterviewRoomPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'copilot';
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState(null);
  const [consent, setConsent] = useState(null);
  const [listen, setListen] = useState(true);
  const [joining, setJoining] = useState(false);
  const media = useMediaStream();
  const dc = useDailyCall();
  const tz = browserTz();
  const load = useCallback(() => roomAPI.get(id).then(({ data }) => { setCtx(data); setConsent(data.candidate_consent); })
    .catch((e) => setErr(errMsg(e, 'Could not open this interview'))), [id]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const joined = dc.state === 'joined';
  const capture = useCopilotCapture({ call: dc.call, joined, enabled: listen && ctx?.role === 'interviewer', bookingId: id, consent: !!consent?.transcription });
  const onConsent = useCallback((c) => setConsent(c), []);
  const join = async () => {
    setJoining(true);
    try {
      const { data } = await roomAPI.join(id);
      media.stop();
      await dc.join({ url: data.room_url, token: data.token });
    } catch (e) {
      toast.error(errMsg(e, 'Could not join the room'));
      load();
    } finally {
      setJoining(false);
    }
  };

  if (err) return <div className="iv vr-room"><div className="vr-body is-solo"><div className="vr-card" data-testid="room-error"><h2>{err}</h2><Link className="vr-back" to="/scheduling"><ArrowLeft size={14} /> Back to scheduling</Link></div></div></div>;
  if (!ctx) return <div className="iv vr-room"><p className="vr-muted" style={{ padding: '2rem' }}>Opening the interview room…</p></div>;
  const b = ctx.booking;
  const [label, tone] = STATE_PILL[ctx.state] || ['', 'slate'];
  return (
    <div className="iv vr-room is-fixed" data-testid="interview-room-page">
      <header className="vr-top">
        <div className="iv-row" style={{ gap: '0.9rem' }}>
          <Link className="vr-back" to="/scheduling" data-testid="room-back-link"><ArrowLeft size={14} /> Scheduling</Link>
          <div>
            <h1 data-testid="room-title">{b.title}: {b.candidate_name}</h1>
            <small>{b.job_title} · {fmtClockTime(b.start, tz)} · {b.duration_min} min · {b.interviewers.map((i) => i.name).join(', ')}</small>
          </div>
        </div>
        <div className="iv-row">
          {ctx.role === 'observer' && <Pill tone="slate" testId="room-observer-pill">Observer</Pill>}
          {consent && <Pill tone={consent.transcription ? 'green' : 'amber'} testId="room-consent-pill">{consent.transcription ? 'Candidate agreed to transcription' : 'Candidate declined transcription'}</Pill>}
          <Pill tone={tone} testId="room-state-pill">{label}</Pill>
        </div>
      </header>
      <div className="vr-body">
        <main className="vr-main">
          <StaffStage ctx={ctx} dc={dc} media={media} joining={joining} onJoin={join} listen={listen} setListen={setListen} tz={tz} />
        </main>
        <Side ctx={ctx} tab={tab} setTab={(k) => setParams({ tab: k }, { replace: true })} capture={capture} live={joined && listen && ctx.role === 'interviewer'} onConsent={onConsent} />
      </div>
    </div>
  );
}
